import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSectionHeadingSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';
import { INDICATOR_INFO, type IndicatorInfo } from '../../components/IndicatorInfoModal/IndicatorInfoModal';

// Renders calculation text with clearer formatting: formula lines (containing " = ") as distinct rows, other lines as paragraphs.
function CalculationBlock({ text, textSizeClass }: { text: string; textSizeClass: string }) {
  const lines = text.split(/\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-4">
      {lines.map((line, i) => {
        const eqIndex = line.indexOf(' = ');
        const looksLikeFormula = eqIndex > 0 && eqIndex < line.length - 3;
        if (looksLikeFormula) {
          const label = line.slice(0, eqIndex);
          const formula = line.slice(eqIndex + 3);
          return (
            <div
              key={i}
              className="flex flex-col gap-1 rounded-lg bg-gray-100/80 dark:bg-gray-800/60 px-4 py-3 border border-gray-200/80 dark:border-gray-700/60 sm:flex-row sm:items-baseline sm:gap-3"
            >
              {/* Label and separator stay on one line; formula wraps at word boundaries only */}
              <span className={`${textSizeClass} font-medium text-text-light-primary dark:text-text-primary whitespace-nowrap flex-shrink-0`}>
                {label}
                <span className="text-text-light-secondary dark:text-text-secondary font-normal">: </span>
              </span>
              <span className={`${textSizeClass} font-mono text-text-light-secondary dark:text-text-secondary break-words min-w-0`}>
                {formula}
              </span>
            </div>
          );
        }
        return (
          <p key={i} className={`${textSizeClass} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
            {line}
          </p>
        );
      })}
    </div>
  );
}

interface FundamentalArticleConfig {
  slug: string; // indicatorKey
  pageTitle: string;
}

const FUNDAMENTAL_ARTICLES: FundamentalArticleConfig[] = [
  { slug: 'pe_ratio', pageTitle: 'P/E Ratio Fundamental Indicator Tutorial' },
  { slug: 'forward_pe_ratio', pageTitle: 'Forward P/E Ratio Fundamental Indicator Tutorial' },
  { slug: 'peg_ratio', pageTitle: 'PEG Ratio Fundamental Indicator Tutorial' },
  { slug: 'ps_ratio', pageTitle: 'P/S Ratio Fundamental Indicator Tutorial' },
  { slug: 'pb_ratio', pageTitle: 'P/B Ratio Fundamental Indicator Tutorial' },
  { slug: 'dividend_yield', pageTitle: 'Dividend Yield Fundamental Indicator Tutorial' },
  { slug: 'diluted_earnings_per_share_adjusted', pageTitle: 'Diluted EPS Fundamental Indicator Tutorial' },
  { slug: 'roa', pageTitle: 'Return on Assets (ROA) Fundamental Indicator Tutorial' },
  { slug: 'roe', pageTitle: 'Return on Equity (ROE) Fundamental Indicator Tutorial' },
  { slug: 'book_value_per_share', pageTitle: 'Book Value Per Share Fundamental Indicator Tutorial' },
  { slug: 'current_ratio', pageTitle: 'Current Ratio Fundamental Indicator Tutorial' },
  { slug: 'quick_ratio', pageTitle: 'Quick Ratio Fundamental Indicator Tutorial' },
  { slug: 'operating_cash_flow', pageTitle: 'Operating Cash Flow Fundamental Indicator Tutorial' },
  { slug: 'investing_cash_flow', pageTitle: 'Investing Cash Flow Fundamental Indicator Tutorial' },
  { slug: 'financing_cash_flow', pageTitle: 'Financing Cash Flow Fundamental Indicator Tutorial' },
  { slug: 'capital_expenditure', pageTitle: 'Capital Expenditure Fundamental Indicator Tutorial' },
  { slug: 'free_cash_flow', pageTitle: 'Free Cash Flow Fundamental Indicator Tutorial' },
  { slug: 'cash_flow_margin', pageTitle: 'Cash Flow Margin Fundamental Indicator Tutorial' },
  { slug: 'ocf_ratio', pageTitle: 'Operating Cash Flow Ratio Fundamental Indicator Tutorial' },
  { slug: 'fcf_yield', pageTitle: 'Free Cash Flow Yield Fundamental Indicator Tutorial' },
];

function buildStockTableDisplayCopy(indicatorKey: string, info: IndicatorInfo): string {
  switch (indicatorKey) {
    case 'pe_ratio':
    case 'forward_pe_ratio':
    case 'peg_ratio':
    case 'ps_ratio':
    case 'pb_ratio':
      return 'Stock Table lines up this valuation metric across your watchlist and uses intuitive arrows to show when valuation is moving in your favor or against you. That makes it easy to spot which stocks are getting cheaper or more expensive relative to their fundamentals without digging through individual reports.';
    case 'dividend_yield':
      return 'In Stock Table, dividend yield sits alongside other valuation metrics with clear arrows that highlight when income potential is rising or falling. This helps income-focused investors quickly zero in on stocks where the yield has become more attractive or is drifting lower over time.';
    case 'diluted_earnings_per_share_adjusted':
      return 'Stock Table surfaces adjusted diluted EPS alongside other profitability metrics and highlights quarter‑over‑quarter or year‑over‑year changes with color‑coded arrows. You can immediately see which companies are growing per‑share earnings on a conservative, fully diluted basis.';
    case 'roa':
    case 'roe':
      return 'Return metrics like ROA and ROE are displayed in Stock Table right next to margins and cash flow, with arrows that show whether returns on assets or equity are improving or deteriorating. This makes it straightforward to compare how efficiently different companies turn capital into profit.';
    case 'book_value_per_share':
      return 'Stock Table tracks book value per share over time and flags increases or decreases using simple arrow coloring. You can quickly see which companies are steadily compounding shareholder equity and which ones are diluting or eroding book value.';
    case 'current_ratio':
    case 'quick_ratio':
      return 'Liquidity ratios in Stock Table are grouped in one place and color‑coded to show whether coverage of short‑term obligations is improving or weakening. That lets you scan a list of tickers and instantly pick out where balance‑sheet strength is building or slipping.';
    case 'operating_cash_flow':
    case 'free_cash_flow':
      return 'Stock Table displays operating and free cash flow together with clear directional arrows so you can see whether real cash generation is trending up or down. Comparing these rows across companies helps you focus on businesses whose cash engines are strengthening rather than just their reported earnings.';
    case 'investing_cash_flow':
    case 'capital_expenditure':
      return 'Investing cash flow and capital expenditure are presented in Stock Table with context‑aware coloring that highlights when spending is ramping up or easing off. This view helps you distinguish between companies aggressively investing for growth and those pulling back to preserve cash.';
    case 'financing_cash_flow':
      return 'Stock Table breaks out financing cash flow so you can quickly see whether a company is raising capital, repaying debt, or returning cash through dividends and buybacks. Directional arrows make it easy to notice shifts in capital allocation behavior over time.';
    case 'cash_flow_margin':
      return 'Cash flow margin appears beside profitability metrics in Stock Table, with arrows that emphasize when cash conversion from revenue is improving or weakening. This layout makes it simple to find companies that are turning a higher share of sales into spendable cash.';
    case 'ocf_ratio':
      return 'Stock Table calculates the operating cash flow ratio for you and highlights movements with clear coloring so you can see whether operating cash comfortably covers current liabilities. That makes short‑term cash strength easy to compare across your entire list of tickers.';
    case 'fcf_yield':
      return 'Free cash flow yield is presented in Stock Table as a percentage you can compare directly to bond yields or across stocks, with arrows that indicate when the cash return at the current price is getting better or worse. This helps you quickly surface potential value ideas based on cash, not just earnings multiples.';
    default:
      return info.coloringLogic || 'Stock Table displays this indicator alongside related metrics and uses straightforward arrows and colors so you can interpret the signal quickly without manual calculations.';
  }
}

const FundamentalIndicatorArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { fontSize } = useFontSize();

  const config = FUNDAMENTAL_ARTICLES.find((item) => item.slug === slug);
  const info = slug ? INDICATOR_INFO[slug] : undefined;

  useEffect(() => {
    if (config && info) {
      document.title = `Stock Table Tutorials - ${info.fullName} Fundamental Indicator`;
    } else {
      document.title = 'Stock Table Tutorials - Fundamental Indicators';
    }
    window.scrollTo(0, 0);
  }, [config, info]);

  if (!config || !info) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
        <Header />
        <main className="flex-grow max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8">
            <h1 className={`${getPageTitleSizeClass(fontSize)} font-bold text-text-light-primary dark:text-text-primary mb-3`}>
              Fundamental Indicator Not Found
            </h1>
            <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary mb-6`}>
              We could not find a tutorial for this fundamental indicator.
            </p>
            <Link
              to="/tutorials/fundamentals"
              className={`inline-block px-5 py-2 rounded-lg bg-primary dark:bg-highlight-gold text-gray-900 font-medium hover:opacity-90 transition-opacity ${getTextSizeClass(fontSize)}`}
            >
              View all fundamental indicator tutorials
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const stockTableDisplayCopy = buildStockTableDisplayCopy(config.slug, info);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />

      <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <article className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8 md:p-10">
          <header className="mb-8">
            <h1 className={`${getPageTitleSizeClass(fontSize)} font-bold text-text-light-primary dark:text-text-primary`}>
              {info.fullName}
            </h1>
          </header>

          <div className="prose prose-sm sm:prose-base max-w-none text-text-light-primary dark:text-text-primary">
            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                Definition and Significance
              </h2>
              <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                {info.description}
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                How it's calculated
              </h2>
              <CalculationBlock text={info.calculation} textSizeClass={getTextSizeClass(fontSize)} />
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                How to interpret the value
              </h2>
              <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                {info.interpretation}
              </p>
            </section>

            <section className="mb-10">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3`}>
                How Stock Table displays {info.fullName}
              </h2>
              <p className={`${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                {stockTableDisplayCopy}
              </p>
              {config.slug === 'pe_ratio' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/pe-ratio-latest-valuation.png"
                      alt="P/E Ratio row in the Latest Valuation section of Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/pe-ratio-fundamentals-tab.png"
                      alt="P/E Ratio displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'forward_pe_ratio' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/forward-pe-ratio-example.png"
                    alt="Forward P/E Ratio row in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'peg_ratio' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/peg-ratio-example.png"
                    alt="PEG Ratio row in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'ps_ratio' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/ps-ratio-latest-valuation.png"
                      alt="P/S Ratio row in the Latest Valuation section of Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/ps-ratio-fundamentals-tab.png"
                      alt="P/S Ratio displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'pb_ratio' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/pb-ratio-latest-valuation.png"
                      alt="P/B Ratio row in the Latest Valuation section of Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/pb-ratio-fundamentals-tab.png"
                      alt="P/B Ratio displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'dividend_yield' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/dividend-yield-latest-valuation.png"
                      alt="Dividend Yield row in the Latest Valuation section of Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/dividend-yield-fundamentals-tab.png"
                      alt="Dividend Yield displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'diluted_earnings_per_share_adjusted' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/diluted-eps-latest-valuation.png"
                      alt="Diluted EPS row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/diluted-eps-fundamentals-tab.png"
                      alt="Diluted EPS displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'roa' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/roa-latest-valuation.png"
                      alt="ROA row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/roa-fundamentals-tab.png"
                      alt="ROA displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'roe' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/roe-latest-valuation.png"
                      alt="ROE row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/roe-fundamentals-tab.png"
                      alt="ROE displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'book_value_per_share' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/book-value-per-share-latest-valuation.png"
                      alt="Book Value Per Share row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/book-value-per-share-fundamentals-tab.png"
                      alt="Book Value Per Share displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'current_ratio' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/current-ratio-latest-valuation.png"
                      alt="Current Ratio row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/current-ratio-fundamentals-tab.png"
                      alt="Current Ratio displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'quick_ratio' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/quick-ratio-latest-valuation.png"
                      alt="Quick Ratio row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/quick-ratio-fundamentals-tab.png"
                      alt="Quick Ratio displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'operating_cash_flow' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/operating-cash-flow-latest-valuation.png"
                      alt="Operating Cash Flow row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/operating-cash-flow-fundamentals-tab.png"
                      alt="Operating Cash Flow displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'investing_cash_flow' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/investing-cash-flow-latest-valuation.png"
                      alt="Investing Cash Flow row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/investing-cash-flow-fundamentals-tab.png"
                      alt="Investing Cash Flow displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'financing_cash_flow' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/financing-cash-flow-latest-valuation.png"
                      alt="Financing Cash Flow row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/financing-cash-flow-fundamentals-tab.png"
                      alt="Financing Cash Flow displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'capital_expenditure' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/capital-expenditure-latest-valuation.png"
                      alt="Capital Expenditure row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/capital-expenditure-fundamentals-tab.png"
                      alt="Capital Expenditure displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'free_cash_flow' && (
                <div className="mt-6 space-y-6">
                  <div className="flex justify-center">
                    <img
                      src="/images/free-cash-flow-latest-valuation.png"
                      alt="Free Cash Flow row in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                  <div className="flex justify-center">
                    <img
                      src="/images/free-cash-flow-fundamentals-tab.png"
                      alt="Free Cash Flow displayed in the Fundamentals tab over time in Stock Table"
                      className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                    />
                  </div>
                </div>
              )}
              {config.slug === 'cash_flow_margin' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/cash-flow-margin-example.png"
                    alt="Cash Flow Margin row in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'ocf_ratio' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/operating-cash-flow-ratio-example.png"
                    alt="Operating Cash Flow Ratio row in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
              {config.slug === 'fcf_yield' && (
                <div className="mt-6 flex justify-center">
                  <img
                    src="/images/free-cash-flow-yield-example.png"
                    alt="Free Cash Flow Yield row in Stock Table"
                    className="max-w-full rounded-lg border border-border-light dark:border-border-color shadow-lg"
                  />
                </div>
              )}
            </section>

            <div className="pt-6 border-t border-border-light dark:border-border-color">
              <Link
                to="/tutorials/fundamentals"
                className={`inline-block px-5 py-2 rounded-lg bg-primary dark:bg-highlight-gold text-gray-900 font-medium hover:opacity-90 transition-opacity ${getTextSizeClass(fontSize)}`}
              >
                Back to fundamental tutorials
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default FundamentalIndicatorArticle;

